<script setup>
import { useEvent } from "@voix/composables/useEventBus";
import { useSliceStore } from "@voix/store/sliceStore";
import { computed } from "vue";
import VoixQuerySlice from "./VoixQuerySlice.vue";
const props = defineProps({
  element: { type: Object, required: true }
});
const sliceStore = useSliceStore();
const voixSliceConfigurations = computed(() => {
  const sliceConfiguration = sliceStore.slices[props.element.component];
  if (!sliceConfiguration) {
    console.error(`Slice ${props.element.component} not found`);
    return null;
  }
  return sliceConfiguration;
});
function getSlotElements(slot) {
  return props.element.elements?.filter((el) => {
    if (!el.slot && slot === "default")
      return true;
    return el.slot === slot;
  });
}
function shouldRenderElement(element) {
  const sliceStore2 = useSliceStore();
  const config = sliceStore2.getSlice(element.component);
  if (config?.xp_management === "manual")
    return true;
  return element.enabled && element.xp_status === "approved" || (typeof element.xp_status === "undefined" || typeof element.enabled === "undefined");
}
function logError(error) {
  let msg = error;
  if (Object.prototype.hasOwnProperty.call(error, "message"))
    msg = error.message;
  if (typeof window === "object" && msg.includes("Failed to fetch dynamically imported module")) {
    let url = window.location.href;
    const queryString = new URLSearchParams(window.location.search);
    if (!queryString.has("v-fresh")) {
      if (url.includes("?"))
        url += "&v-fresh=1";
      else
        url += "?v-fresh=1";
      window.location.href = url;
    }
  }
  useEvent("voix:error", error);
  console.error(error);
  return error;
}
</script>

<template>
  <template v-if="props.element && props.element.type === 'slice' && shouldRenderElement(element)">
    <NuxtErrorBoundary @error="logError">
      <template v-if="voixSliceConfigurations">
        <LazyVoixSlice
          v-if="props.element.hydration_mode === 'idle'"
          hydrate-on-idle
          :slice="element"
        >
          <template
            v-for="{ name } in voixSliceConfigurations.slots"
            :key="name"
            #[name]
          >
            <LazyVoixElementsLoop :elements="getSlotElements(name)" />
          </template>
        </LazyVoixSlice>
        <LazyVoixSlice
          v-else-if="props.element.hydration_mode === 'mouseover'"
          :slice="element"
          hydrate-on-interaction="mouseover"
        >
          <template
            v-for="{ name } in voixSliceConfigurations.slots"
            :key="name"
            #[name]
          >
            <LazyVoixElementsLoop :elements="getSlotElements(name)" />
          </template>
        </LazyVoixSlice>
        <LazyVoixSlice
          v-else-if="props.element.hydration_mode === 'visible'"
          hydrate-on-visible
          :slice="element"
        >
          <template
            v-for="{ name } in voixSliceConfigurations.slots"
            :key="name"
            #[name]
          >
            <LazyVoixElementsLoop :elements="getSlotElements(name)" />
          </template>
        </LazyVoixSlice>
        <LazyVoixSlice
          v-else-if="props.element.hydration_mode === 'never'"
          hydrate-never
          :slice="element"
        >
          <template
            v-for="{ name } in voixSliceConfigurations.slots"
            :key="name"
            #[name]
          >
            <LazyVoixElementsLoop :elements="getSlotElements(name)" />
          </template>
        </LazyVoixSlice>
        <ClientOnly v-else-if="props.element.hydration_mode === 'client-only'">
          <LazyVoixSlice :slice="element">
            <template
              v-for="{ name } in voixSliceConfigurations.slots"
              :key="name"
              #[name]
            >
              <LazyVoixElementsLoop :elements="getSlotElements(name)" />
            </template>
          </LazyVoixSlice>
        </ClientOnly>
        <ClientOnly v-else-if="props.element.hydration_mode === 'client-only-when-visible'">
          <LazyVoixSlice
            hydrate-on-visible
            :slice="element"
          >
            <template
              v-for="{ name } in voixSliceConfigurations.slots"
              :key="name"
              #[name]
            >
              <LazyVoixElementsLoop :elements="getSlotElements(name)" />
            </template>
          </LazyVoixSlice>
        </ClientOnly>
        <LazyVoixSlice
          v-else
          :slice="element"
        >
          <template
            v-for="{ name } in voixSliceConfigurations.slots"
            :key="name"
            #[name]
          >
            <LazyVoixElementsLoop :elements="getSlotElements(name)" />
          </template>
        </LazyVoixSlice>
      </template>
    </NuxtErrorBoundary>
  </template>

  <VoixQuerySlice
    v-if="props.element.type === 'query' && shouldRenderElement(element)"
    :element="props.element"
  />
</template>
